import React, { useState } from 'react';
import {
  LayoutDashboard,
  Inbox,
  ListCollapse,
  UserCheck,
  Menu,
  X,
  MessageSquare,
  ClipboardList,
  Settings,
  ChevronRight,
  LogOut,
  User,
  Bell,
} from 'lucide-react';

interface BottomNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

interface BottomNavigationProps {
  currentTab: string;
  onTabChange: (tab: string) => void;
  onLogout: () => void;
  tabsAvailable: string[];
  demands?: any[];
  unreadCount?: number;
  userName?: string;
  userRole?: string;
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  currentTab,
  onTabChange,
  onLogout,
  tabsAvailable,
  demands = [],
  userName = '',
  userRole = '',
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const inboxBadge = demands.filter((d: any) => d.status === 'RECEBIDA').length;

  const mainTabs: BottomNavItem[] = [
    { id: 'dashboard', label: 'Início',    icon: <LayoutDashboard className="w-5 h-5" /> },
    { id: 'inbox',     label: 'Triagem',   icon: <Inbox className="w-5 h-5" />, badge: inboxBadge },
    { id: 'demands',   label: 'Demandas',  icon: <ListCollapse className="w-5 h-5" /> },
    { id: 'contacts',  label: 'Contatos',  icon: <UserCheck className="w-5 h-5" /> },
  ].filter(tab => tabsAvailable.includes(tab.id));

  // Ensure max 4 main tabs + "Mais"
  const visibleTabs = mainTabs.slice(0, 4);

  const moreItems = [
    { id: 'profile',      label: 'Meu Perfil',              icon: <User className="w-5 h-5 text-indigo-500" /> },
    { id: 'alerts',       label: 'Alertas',                 icon: <Bell className="w-5 h-5 text-amber-500" /> },
    { id: 'integrations', label: 'WhatsApp / Integrações', icon: <MessageSquare className="w-5 h-5 text-emerald-500" /> },
    { id: 'audit',        label: 'Auditoria & Logs',        icon: <ClipboardList className="w-5 h-5 text-violet-500" /> },
    { id: 'users',        label: 'Configurações',            icon: <Settings className="w-5 h-5 text-slate-500" /> },
  ].filter(item => tabsAvailable.includes(item.id) || item.id === 'alerts');

  const isMoreActive = !visibleTabs.find(t => t.id === currentTab) && currentTab !== 'dashboard';

  const handleTabClick = (id: string) => {
    setIsMoreOpen(false);
    onTabChange(id);
  };

  return (
    <>
      {/* Backdrop for "Mais" drawer */}
      {isMoreOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40 fade-backdrop"
          onClick={() => setIsMoreOpen(false)}
        />
      )}

      {/* "Mais" Drawer */}
      {isMoreOpen && (
        <div className="md:hidden fixed bottom-[60px] left-0 right-0 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-t-3xl shadow-2xl slide-up px-4 pt-5 pb-6 safe-bottom">
          {/* Handle */}
          <div className="w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto mb-5" />

          {/* User chip */}
          <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-slate-800/60 rounded-2xl mb-4">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs text-white shrink-0"
              style={{ background: 'linear-gradient(135deg, #1B2D72 0%, #1565C0 100%)' }}
            >
              {userName.charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate">{userName}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide font-mono">{userRole.replace('ROLE_', '').replace(/_/g, ' ')}</p>
            </div>
          </div>

          {/* More items */}
          <div className="space-y-1">
            {moreItems.map(item => (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                className={`w-full flex items-center justify-between p-3.5 rounded-2xl transition-all text-left touch-target ${
                  currentTab === item.id
                    ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-600'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  {item.icon}
                  <span className="text-sm font-semibold">{item.label}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100 dark:border-slate-800 my-3" />

          {/* Logout */}
          <button
            onClick={() => { setIsMoreOpen(false); onLogout(); }}
            className="w-full flex items-center gap-3 p-3.5 rounded-2xl text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all touch-target"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-semibold">Sair do Sistema</span>
          </button>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-950/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 safe-bottom">
        <div className="flex items-stretch h-[60px]">
          {visibleTabs.map(tab => {
            const isActive = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-all touch-target ${
                  isActive ? 'bottom-nav-active' : 'text-slate-400 dark:text-slate-500'
                }`}
              >
                {/* Badge */}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="absolute top-2 right-[calc(50%-10px)] w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {tab.badge > 9 ? '9+' : tab.badge}
                  </span>
                )}
                {tab.icon}
                <span className="text-[9px] font-semibold">{tab.label}</span>
                {isActive && <div className="nav-dot" />}
              </button>
            );
          })}

          {/* Mais */}
          <button
            onClick={() => setIsMoreOpen(!isMoreOpen)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-all touch-target ${
              isMoreActive ? 'bottom-nav-active' : 'text-slate-400 dark:text-slate-500'
            }`}
          >
            {isMoreOpen
              ? <X className="w-5 h-5" />
              : <Menu className="w-5 h-5" />
            }
            <span className="text-[9px] font-semibold">Mais</span>
            {isMoreActive && <div className="nav-dot" />}
          </button>
        </div>
      </nav>
    </>
  );
};
